/**
 * The turn banner above the board: who is to move right now, and what the last
 * player actually did. It reads everything from the game log in state, so it
 * works identically in hotseat, vs-computer, and online play. The inner content
 * is keyed on the ply so it remounts (and re-announces via aria-live) each turn,
 * giving a clear "it's changed" pulse.
 */
import { Show } from "solid-js";
import type { GameController } from "../../app/controller";
import { PLAYER_ACCENT } from "../../app/config";
import { isInCheck, type PlayerId } from "../../core";
import { describeLogEntry } from "../lib/describe";

export function TurnBanner(props: {
  controller: GameController;
  /** Online or vs-computer: the view is locked to one seat ("you"). */
  perspective: boolean;
  /** Watching someone else's game — no "you", use player names. */
  spectator?: boolean;
  online?: boolean;
  aiThinking?: () => boolean;
}) {
  const c = props.controller;
  const state = () => c.store.state;
  const turn = () => state().turn;

  // The seat the viewer controls, or null when there is no single "you"
  // (hotseat / spectator / replay).
  const youSeat = (): PlayerId | null =>
    props.perspective && !props.spectator ? c.mySeat : null;

  const actorLabel = (seat: PlayerId): string => {
    if (seat === youSeat()) return "You";
    if (props.perspective && !props.online && !props.spectator) return "Computer";
    return state().players[seat].name;
  };

  const lastText = (): string | null => {
    const log = state().log;
    const e = log[log.length - 1];
    if (!e) return null;
    // The turn-limit entry has no actor — it's a neutral system event.
    if (!("player" in e)) return `${describeLogEntry(e, state())}.`;
    return `${actorLabel(e.player)} ${describeLogEntry(e, state())}.`;
  };

  const inCheck = () => isInCheck(state(), turn());

  const status = (): string => {
    const you = youSeat();
    if (you !== null) {
      if (turn() === you) return inCheck() ? "Your move — you're in check!" : "Your move";
      if (props.aiThinking?.()) return "Computer is thinking…";
      return `${state().players[turn()].name} to move`;
    }
    const name = state().players[turn()].name;
    return inCheck() ? `${name} to move — in check!` : `${name} to move`;
  };

  return (
    <Show when={state().phase === "playing"}>
      {/* keyed on ply (+1 so it's truthy on ply 0) so each new turn remounts ->
          entrance pulse + re-announce */}
      <Show when={state().ply + 1} keyed>
        <div
          class="turn-banner"
          classList={{ mine: youSeat() !== null && turn() === youSeat(), check: inCheck() }}
        >
          <span class="tb-status" style={{ color: PLAYER_ACCENT[turn()] }}>
            {status()}
          </span>
          <Show when={lastText()}>
            <span class="tb-last" aria-live="polite">
              {lastText()}
            </span>
          </Show>
        </div>
      </Show>
    </Show>
  );
}
